<template>
    <div class="pa-5">
        <v-divider />
        <v-row class="justify-end">
            <v-col :cols="2">
                <v-row class="mr-1">
                    <v-btn
                        small
                        :color="chartType && chartType === 'month' ? 'bal-red' : null"
                        :dark="chartType && chartType === 'month'"
                        @click="setChartType('month')"
                        >{{ "Month" | t }}</v-btn
                    >
                    <v-spacer />
                    <v-btn
                        small
                        :color="chartType && chartType === 'week' ? 'bal-red' : null"
                        :dark="chartType && chartType === 'week'"
                        @click="setChartType('week')"
                        >{{ "Week" | t }}</v-btn
                    >
                </v-row>
            </v-col>
        </v-row>
        <highcharts v-if="activeShop" ref="chart" :options="lineChartOptions" :updateArgs="updateArgs" />
    </div>
</template>

<script>
import { Chart } from "highcharts-vue";
import { mapMutations, mapState } from "vuex";
import moment from "moment";

export default {
    name: "chart-view",
    components: {
        highcharts: Chart
    },
    data() {
        let self = this;
        return {
            productsArray: [],
            updateArgs: [true, true, { duration: 500 }],
            lineChartOptions: {
                xAxis: {
                    categories: [
                        this.t("January"),
                        this.t("February"),
                        this.t("March"),
                        this.t("April"),
                        this.t("May"),
                        this.t("June"),
                        this.t("July"),
                        this.t("August"),
                        this.t("September"),
                        this.t("October"),
                        this.t("November"),
                        this.t("December")
                    ]
                },
                yAxis: {
                    title: { text: "€" }
                },
                credits: {
                    enabled: false
                },
                chart: {
                    type: "line"
                },

                title: {
                    text: null
                },
                tooltip: {
                    formatter: function() {
                        let productString = "";
                        for (let product of self.productsArray[this.point.x]) {
                            if (product && product.name) {
                                productString += `<b>${product.name}</b> - ${self.rn(product.sum, 2)}€<br/>`;
                            }
                        }
                        return `<b>${self.t(this.point.category)}</b><br/>` + `${this.y}€<br/>` + productString;
                    }
                },
                plotOptions: {
                    column: {
                        stacking: "normal"
                    },
                    series: {
                        events: {
                            legendItemClick: function(e) {
                                e.preventDefault();
                            }
                        }
                    }
                },
                series: []
            }
        };
    },
    computed: {
        ...mapState("ShopsAdminStore", ["activeShop", "chartType"])
    },
    mounted() {
        if (this.chartType === "month") this.getLineChartMonthData();
        else this.getLineChartWeekData();
    },
    watch: {
        activeShop: function() {
            if (this.chartType === "month") this.getLineChartMonthData();
            else this.getLineChartWeekData();
        },
        chartType: function() {
            if (this.chartType === "month") this.getLineChartMonthData();
            else this.getLineChartWeekData();
        }
    },
    methods: {
        ...mapMutations("ShopsAdminStore", ["setChartType"]),
        getLineChartWeekData() {
            this.lineChartOptions.series = [];
            let weekArray = [];
            let lastDate = this.activeShop.import_rows[this.activeShop.import_rows.length - 1].invoice_date;
            for (let i = 1; i <= 52; i++) {
                weekArray.push(i);
            }
            for (let i = 0; i < 52; i++) {
                weekArray.unshift(
                    moment(lastDate, "YYYY-MM-DD")
                        .subtract(i, "week")
                        .format("w YYYY")
                );
            }
            let totalNet = new Array(52).fill(0);
            this.productsArray = [];
            for (let i = 0; i < 52; i++) {
                this.productsArray.push([]);
            }
            for (let i = 0; i < this.activeShop.import_rows.length; i++) {
                let invoiceDate = moment(this.activeShop.import_rows[i].invoice_date, "YYYY-MM-DD");
                let index = weekArray.indexOf(invoiceDate.format("w YYYY"));

                if (totalNet[index]) {
                    totalNet[index] += this.activeShop.import_rows[i].total_net;
                } else {
                    totalNet[index] += this.activeShop.import_rows[i].total_net || 0;
                }
                if (index === -1) continue;
                let alreadyExists = this.productsArray[index].find(
                    x => x.name === this.activeShop.import_rows[i].product
                );
                if (alreadyExists) {
                    alreadyExists.sum += this.activeShop.import_rows[i].total_net;
                } else {
                    this.productsArray[index].push({
                        name: this.activeShop.import_rows[i].product,
                        sum: this.activeShop.import_rows[i].total_net || 0
                    });
                }
            }
            totalNet = totalNet.map(totalNet => Math.round(totalNet * 100) / 100);
            let arrayLastIndex = totalNet.length - 1;
            let untilFirstRealNumber = true;
            while (untilFirstRealNumber) {
                if (totalNet[arrayLastIndex] === 0) {
                    totalNet.pop();
                    weekArray.pop();
                    this.productsArray.pop();
                    arrayLastIndex--;
                } else {
                    untilFirstRealNumber = false;
                }
            }
            let arrayFirstIndex = 0;
            untilFirstRealNumber = true;
            while (untilFirstRealNumber) {
                if (totalNet[arrayFirstIndex] === 0) {
                    weekArray.shift();
                    totalNet.shift();
                    this.productsArray.shift();
                } else {
                    untilFirstRealNumber = false;
                }
            }

            let sumParam = {
                name: this.t("Net sum"),
                data: totalNet,
                color: "#000000"
            };
            weekArray = weekArray.splice(0, totalNet.length).map(name => {
                let dateName = name.split(" ");
                return parseInt(dateName[1]) === new Date().getFullYear()
                    ? `${dateName[0]}`
                    : `${dateName[0]} (${dateName[1]})`;
            });
            this.lineChartOptions.series.push(sumParam);
            this.lineChartOptions.xAxis.categories = weekArray;
        },
        getLineChartMonthData() {
            this.lineChartOptions.series = [];
            let lastDate = this.activeShop.import_rows[this.activeShop.import_rows.length - 1].invoice_date;
            let monthArray = [];
            for (let i = 0; i < 12; i++) {
                monthArray.unshift(
                    moment(lastDate, "YYYY-MM-DD")
                        .subtract(i, "month")
                        .format("MMMM YYYY")
                );
            }
            let totalNet = new Array(12).fill(0);
            this.productsArray = [];
            for (let i = 0; i < 12; i++) {
                this.productsArray.push([]);
            }
            for (let i = 0; i < this.activeShop.import_rows.length; i++) {
                let invoiceDate = moment(this.activeShop.import_rows[i].invoice_date, "YYYY-MM-DD");
                let index = monthArray.indexOf(invoiceDate.format("MMMM YYYY"));
                if (totalNet[index]) {
                    totalNet[index] += this.activeShop.import_rows[i].total_net;
                } else {
                    totalNet[index] += this.activeShop.import_rows[i].total_net || 0;
                }
                if (index === -1) continue;
                let alreadyExists = this.productsArray[index].find(
                    x => x.name === this.activeShop.import_rows[i].product
                );
                if (alreadyExists) {
                    alreadyExists.sum += this.activeShop.import_rows[i].total_net;
                } else {
                    this.productsArray[index].push({
                        name: this.activeShop.import_rows[i].product,
                        sum: this.activeShop.import_rows[i].total_net || 0
                    });
                }
            }
            totalNet = totalNet.map(totalNet => Math.round(totalNet * 100) / 100);
            let arrayLastIndex = totalNet.length - 1;
            let untilFirstRealNumber = true;
            while (untilFirstRealNumber) {
                if (totalNet[arrayLastIndex] === 0) {
                    totalNet.pop();
                    this.productsArray.pop();
                    monthArray.pop();
                    arrayLastIndex--;
                } else {
                    untilFirstRealNumber = false;
                }
            }
            let arrayFirstIndex = 0;
            untilFirstRealNumber = true;
            while (untilFirstRealNumber) {
                if (totalNet[arrayFirstIndex] === 0) {
                    monthArray.shift();
                    totalNet.shift();
                    this.productsArray.shift();
                } else {
                    untilFirstRealNumber = false;
                }
            }

            let sumParam = {
                name: this.t("Net sum"),
                data: totalNet,
                color: "#000000",
                muu: totalNet
            };
            monthArray = monthArray.map(name => {
                let dateName = name.split(" ");
                return parseInt(dateName[1]) === new Date().getFullYear()
                    ? `${this.t(dateName[0])}`
                    : `${this.t(dateName[0])} ${dateName[1]}`;
            });
            this.lineChartOptions.series.push(sumParam);
            this.lineChartOptions.xAxis.categories = monthArray;
        }
    }
};
</script>

<style scoped></style>
